// @file surface collections store
import { useCollectionsStore } from '@@/pinia/collections_store'
import { useSurfaceAccountsStore } from '@@/pinia/surface_accounts_store'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import type { AccountKey, Library } from '@@/types'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useSurfaceCollectionsStore = defineStore('surfaceCollectionsStore', () => {
  // External stores, assuming these are initialized elsewhere in surface
  const collectionsStore = useCollectionsStore()
  const currentUserStore = useSurfaceCurrentUserStore()
  const surfaceAccountsStore = useSurfaceAccountsStore()

  // create current user account key from current user id
  const currentUserAccountKey = computed(() =>
    currentUserStore.currentUser == null ? null : { type: 'user', id: currentUserStore.currentUser?.id },
  )
  // create current library from default personal library or library for current account id
  const currentLibrary = computed((): Partial<Library> => {
    if (collectionsStore.currentAccountKey == null || collectionsStore.currentAccountKey.type === 'user')
      return surfaceAccountsStore.defaultPersonalLibrary
    return (
      surfaceAccountsStore.librariesById[collectionsStore.currentAccountKey.id] ??
      surfaceAccountsStore.defaultPersonalLibrary
    )
  })

  // initialize shared collections store with computed refs from surface stores
  function initialize(): void {
    collectionsStore.initialize({
      currentUserAccountKey: currentUserAccountKey.value as AccountKey,
      currentLibrary: currentLibrary.value as Library,
    })
  }

  const isActiveWallBookmarked = computed(() => collectionsStore.isActiveWallBookmarked)
  const activeWallId = computed(() => collectionsStore.activeWallId)
  const activeUserFolders = computed(() => collectionsStore.activeUserFolders)
  const activeWallFolderIds = computed(() => collectionsStore.activeWallFolderIds)
  const isActiveWallInFavorites = computed(() => collectionsStore.isActiveWallInFavorites)
  const activeCollectionWalls = computed(() => collectionsStore.activeCollectionWalls)
  const isActiveCollectionFetching = computed(() => collectionsStore.isActiveCollectionFetching)
  const isActiveCollectionFetched = computed(() => collectionsStore.isActiveCollectionFetched)
  const isActiveCollectionFetchErrored = computed(() => collectionsStore.isActiveCollectionFetchErrored)
  const currentAccountKey = computed(() => collectionsStore.currentAccountKey)
  const activeCollectionKey = computed(() => collectionsStore.activeCollectionKey)
  const usersCollectionsWallIds = computed(() => collectionsStore.usersCollectionsWallIds)
  const activeCollectionDateSortAttribute = computed(() => collectionsStore.activeCollectionDateSortAttribute)
  const emptyActiveCollectionCopy = computed(() => collectionsStore.emptyActiveCollectionCopy)

  // Re-export everything from collections store
  return {
    currentLibrary,
    currentUserAccountKey,

    // Getters
    isActiveWallBookmarked,
    activeWallId,
    activeUserFolders,
    activeWallFolderIds,
    isActiveWallInFavorites,
    activeCollectionWalls,
    isActiveCollectionFetching,
    isActiveCollectionFetched,
    isActiveCollectionFetchErrored,
    currentAccountKey,
    activeCollectionKey,
    usersCollectionsWallIds,
    activeCollectionDateSortAttribute,
    emptyActiveCollectionCopy,

    // Actions
    initialize, // initializes the nested collections store
    getIsCollectionFetching: collectionsStore.getIsCollectionFetching,
    getIsCollectionFetched: collectionsStore.getIsCollectionFetched,
    getIsCollectionFetchErrored: collectionsStore.getIsCollectionFetchErrored,
    getCollectionWalls: collectionsStore.getCollectionWalls,
    getEmptyCollectionCopy: collectionsStore.getEmptyCollectionCopy,
    addBookmark: collectionsStore.addBookmark,
    removeBookmark: collectionsStore.removeBookmark,
    setActiveUser: collectionsStore.setActiveUser,
    switchAccountKey: collectionsStore.switchAccountKey,
    setActiveWall: collectionsStore.setActiveWall,
    fetchBookmarks: collectionsStore.fetchBookmarks,
    fetchFolders: collectionsStore.fetchFolders,
    createFolderForActiveUser: collectionsStore.createFolderForActiveUser,
    fetchActiveCollectionWallsNow: collectionsStore.fetchActiveCollectionWallsNow,
    setAccountCollectionCursor: collectionsStore.setAccountCollectionCursor,
    fetchCollectionWalls: collectionsStore.fetchCollectionWalls,
    fetchSearchResults: collectionsStore.fetchSearchResults,
  }
})
